import React, { FC } from 'react';
import { ScrollView, View } from 'react-native';

import ColorWrapper, { WithColor } from '@styles/colors';
import { scale } from '@components/frame';
import { ContainerStyle } from '@interfaces/base';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import type { DashboardPerScopeValueResponse, DashboardResult } from '@api/domain/types';
import { DashboardScope, DashboardResultType } from '@api/domain/types';

import DashboardOverviewBottomBlockPanel from './components/DashboardOverviewBottomBlockPanel';
import { useManageDashboardOverviewBottomBlock } from './hooks/useManageDashboardOverviewBottomBlock';
import { RankBadgeVariant } from '@v2/pages/dashboard/common/types/rows';
import { isPhone } from '@helpers';

export interface DashboardOverviewBottomBlockProps extends WithColor, ContainerStyle {
  scope: DashboardScope;
  resultType: DashboardResultType;
  worst: DashboardPerScopeValueResponse | null;
  best: DashboardPerScopeValueResponse | null;
  worstLoading: boolean;
  bestLoading: boolean;
  worstError: string | null;
  bestError: string | null;
  onShowAllWorst?: () => void;
  onShowAllBest?: () => void;
  onShowAllActual?: () => void;
  onRowPress: (item: DashboardResult) => void;
}

const DashboardOverviewBottomBlock: FC<DashboardOverviewBottomBlockProps> = ({
  C,
  scope,
  resultType,
  worst,
  best,
  worstLoading,
  bestLoading,
  worstError,
  bestError,
  onShowAllWorst,
  onShowAllBest,
  onShowAllActual,
  onRowPress,
  containerStyle,
}) => {
  const { worstResults, bestResults } = useManageDashboardOverviewBottomBlock({ worst, best });

  const isActual = resultType === DashboardResultType.Actual;

  const panels = isActual
    ? [
        <DashboardOverviewBottomBlockPanel
          key="actual"
          variant={RankBadgeVariant.Actual}
          scope={scope}
          results={bestResults}
          loading={bestLoading}
          error={bestError}
          onShowAll={onShowAllActual}
          onRowPress={onRowPress}
        />,
      ]
    : [
        <DashboardOverviewBottomBlockPanel
          key="worst"
          variant={RankBadgeVariant.Worst}
          scope={scope}
          results={worstResults}
          loading={worstLoading}
          error={worstError}
          onShowAll={onShowAllWorst}
          onRowPress={onRowPress}
        />,
        <DashboardOverviewBottomBlockPanel
          key="best"
          variant={RankBadgeVariant.Best}
          scope={scope}
          results={bestResults}
          loading={bestLoading}
          error={bestError}
          onShowAll={onShowAllBest}
          onRowPress={onRowPress}
        />,
      ];

  if (isPhone) {
    return (
      <ScrollView
        testID={TEST_IDS.dashboardOverviewBottomBlock.container}
        style={[{ flex: 1 }, containerStyle]}
        contentContainerStyle={[{ paddingHorizontal: scale(2) }, !isActual && { gap: scale(16) }]}
        showsVerticalScrollIndicator={false}
      >
        {panels}
      </ScrollView>
    );
  }

  return (
    <View
      testID={TEST_IDS.dashboardOverviewBottomBlock.container}
      style={[!isActual && { flexDirection: 'row', gap: scale(16) }, { flex: 1 }, containerStyle]}
    >
      {panels}
    </View>
  );
};

export default ColorWrapper(DashboardOverviewBottomBlock);
